import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { workOrdersApi } from 'features/workOrders/workOrdersApi';
import { workOrdersDocumentsApi } from 'features/workOrders/workOrdersDocumentsApi';
import { usersApi } from 'features/users/usersApi';
import { projectsApi } from 'features/projects/projectsApi';
import { companyApi } from 'features/company/companyApi';
import { notificationsApi } from 'features/notifications/notificationsApi';
import { authApi } from 'features/auth/authApi';
import { projectsTemplatesApi } from 'features/projectsTemplates/projectsTemplatesApi';
import { leadsApi } from 'features/leads/leadsApi';
import { leadsTemplatesApi } from 'features/leadsTemplates/leadsTemplatesApi';
import { leadsTemplatesPublicApi } from 'features/leadsTemplates/leadsTemplatesPublicApi';
import { proposalsApi } from 'features/proposals/proposalsApi';
import { customersApi } from 'features/customers/customersApi';
import { onboardingApi } from 'features/onboarding/onboardingApi';
import { budgetTemplatesApi } from 'features/budgetTemplates/budgetTemplatesApi';
import { quoteTemplatesApi } from 'features/quoteTemplates/quoteTemplatesApi';
import { budgetsApi } from 'features/budgets/budgetsApi';
import { inventoryApi } from 'features/inventory/inventoryApi';
import { accountingApi } from 'features/accounting/accountingApi';
import { expensesApi } from 'features/expenses/expensesApi';
import { adminApi } from 'features/admin/adminApi';
import { rolesApi } from 'features/roles/rolesApi';
import { projectChatApi } from 'features/projectChat/projectChatApi';
import { partnersApi } from 'features/partners/partnersApi';

import uiReducer from '../features/ui/uiSlice';
import authReducer, { clearAuth } from '../features/auth/authSlice';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';

const appReducer = combineReducers({
  ui: uiReducer,
  auth: authReducer,
  [workOrdersApi.reducerPath]: workOrdersApi.reducer,
  [workOrdersDocumentsApi.reducerPath]: workOrdersDocumentsApi.reducer,
  [usersApi.reducerPath]: usersApi.reducer,
  [projectsApi.reducerPath]: projectsApi.reducer,
  [companyApi.reducerPath]: companyApi.reducer,
  [notificationsApi.reducerPath]: notificationsApi.reducer,
  [authApi.reducerPath]: authApi.reducer,
  [projectsTemplatesApi.reducerPath]: projectsTemplatesApi.reducer,
  [leadsApi.reducerPath]: leadsApi.reducer,
  [leadsTemplatesApi.reducerPath]: leadsTemplatesApi.reducer,
  [leadsTemplatesPublicApi.reducerPath]: leadsTemplatesPublicApi.reducer,
  [proposalsApi.reducerPath]: proposalsApi.reducer,
  [customersApi.reducerPath]: customersApi.reducer,
  [onboardingApi.reducerPath]: onboardingApi.reducer,
  [budgetTemplatesApi.reducerPath]: budgetTemplatesApi.reducer,
  [quoteTemplatesApi.reducerPath]: quoteTemplatesApi.reducer,
  [budgetsApi.reducerPath]: budgetsApi.reducer,
  [inventoryApi.reducerPath]: inventoryApi.reducer,
  [accountingApi.reducerPath]: accountingApi.reducer,
  [expensesApi.reducerPath]: expensesApi.reducer,
  [adminApi.reducerPath]: adminApi.reducer,
  [rolesApi.reducerPath]: rolesApi.reducer,
  [projectChatApi.reducerPath]: projectChatApi.reducer,
  [partnersApi.reducerPath]: partnersApi.reducer,
});

const rootReducer: typeof appReducer = (state, action) => {
  if (action.type === clearAuth.type) {
    return appReducer(undefined, action);
  }
  return appReducer(state, action);
};

export const store = configureStore({
  devTools: process.env.NODE_ENV !== 'production',
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }).concat(
      workOrdersApi.middleware,
      workOrdersDocumentsApi.middleware,
      usersApi.middleware,
      projectsApi.middleware,
      customersApi.middleware,
      companyApi.middleware,
      notificationsApi.middleware,
      authApi.middleware,
      projectsTemplatesApi.middleware,
      leadsApi.middleware,
      leadsTemplatesApi.middleware,
      leadsTemplatesPublicApi.middleware,
      proposalsApi.middleware,
      onboardingApi.middleware,
      budgetTemplatesApi.middleware,
      quoteTemplatesApi.middleware,
      budgetsApi.middleware,
      inventoryApi.middleware,
      accountingApi.middleware,
      expensesApi.middleware,
      adminApi.middleware,
      rolesApi.middleware,
      projectChatApi.middleware,
      partnersApi.middleware,
    ),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
